//@ts-nocheck
import { ChevronLeftIcon, Search2Icon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Button,
  Center,
  Flex,
  HStack,
  Icon as CIcon,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
  useCheckbox,
  useCheckboxGroup,
  UseCheckboxProps,
  useColorModeValue as mode,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import Pluralize from "pluralize";
import React, { useState } from "react";
import { BiRocket } from "react-icons/bi";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import Icon from "../components/ui-elements/Icon";
// import { EnterIcon, ErrorIcon } from "../icons";
import { ISubjectStore, ITuteriaGroup } from "../stores";
import { arrayToString } from "../utils/functions";
import subjectData from "./formData/subject.json";
import { displayToast } from "./forms/SharedComponents";
import { SubjectCheckbox } from "./SubjectCheckbox";
import { SubjectCreationTimer } from "./SubjectPrompt";

export const CheckboxBox = (props: BoxProps) => (
  <Box
    borderWidth="2px"
    borderColor={"neutral.600"}
    px="4"
    py="3"
    borderRadius="md"
    cursor="pointer"
    transition="all 0.2s"
    _focus={{ shadow: "outline" }}
    _checked={{
      bg: mode("gray.50", "whiteAlpha.100"),
      borderColor: mode("blue.500", "blue.300"),
    }}
    {...(props as any)}
  />
);

interface ButtonCheckboxProps extends UseCheckboxProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

export const ButtonCheckbox = (props: ButtonCheckboxProps) => {
  const { title, description, ...rest } = props;
  const { getCheckboxProps, getInputProps, getLabelProps, state } =
    useCheckbox(rest);
  return (
    <label {...getLabelProps()}>
      <input {...getInputProps()} />
      <CheckboxBox {...(getCheckboxProps() as any)} {...rest}>
        <HStack spacing="4">
          <Box
            data-checked={state.isChecked ? "" : undefined}
            fontSize="2xl"
            _checked={{
              color: mode("blue.500", "blue.300"),
            }}
            color={mode("gray.300", "whiteAlpha.400")}
          >
            {state.isChecked ? <MdCheckBox /> : <MdCheckBoxOutlineBlank />}
          </Box>
          <Box flex="1">
            <Text>{title}</Text>
            {description && <Text fontSize="sm">{description}</Text>}
          </Box>
        </HStack>
      </CheckboxBox>
    </label>
  );
};

const SubjectGroupDetails: React.FC<{
  store: ISubjectStore;
  currentGroup: ITuteriaGroup & { subjects: any };
  selectSubject: (params: any[]) => void;
}> = observer(({ currentGroup, selectSubject, store }) => {
  const { getCheckboxProps } = useCheckboxGroup({
    defaultValue: store.selectedSubjectGroups,
    onChange: (value) => selectSubject([...value]),
  });

  return (
    <Box as="section">
      <Stack spacing="5" justify="flex-start" mb={6}>
        {currentGroup.subjects.map((item) => {
          return (
            <ButtonCheckbox
              {...getCheckboxProps({ value: item.name })}
              key={item.name}
              isDisabled={store.isSelectedSubjectGroup(item.name)}
              title={item.name}
            >
              {item.name}
            </ButtonCheckbox>
          );
        })}
      </Stack>
    </Box>
  );
});

const SubjectGroupList: React.FC<{
  store: ISubjectStore;
  groups: Array<ITuteriaGroup & { subjects: any }>;
  displayDetails: (params: ITuteriaGroup) => void;
}> = observer(({ groups, displayDetails, store }) => {
  return (
    <Stack spacing={5} pb={6}>
      {groups.map((groupItem) => {
        return (
          <Box
            key={groupItem.title}
            width="full"
            borderWidth="xs"
            borderColor="neutral.500"
            borderRadius="md"
            px={6}
            py={4}
            cursor={"pointer"}
            _hover={{ bg: "neutral.100" }}
            transition="all 0.2s"
            _focus={{ shadow: "outline" }}
            onClick={() => displayDetails(groupItem)}
          >
            <Box>{groupItem.title}</Box>
            <Text color="gray.600" isTruncated>
              {arrayToString(groupItem.subjects.map((x) => x.name))}
            </Text>
          </Box>
        );
      })}
    </Stack>
  );
});

export const SubjectGroupSelection: React.FC<{
  store: ISubjectStore;
}> = observer(({ store }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [isDetails, setIsDetails] = useState(false);
  const canDisplaySubjectPrompt = (subjects: any) => {
    const hasActiveSubjects = subjects.filter(
      (subject) => subject.status === "active",
    );
    const hasPendingSubjects = subjects.filter(
      (subject) => subject.status === "pending",
    );
    return hasActiveSubjects || hasPendingSubjects;
  };
  const displaySubjectCreation =
    canDisplaySubjectPrompt(store.tutorSubjects) &&
    store.generatedGroups.length > 0;

  if (!store.canAddMoreSubjects) {
    return <div />;
  }

  return (
    <Stack>
      <Box
        rounded="lg"
        borderWidth="2xs"
        borderColor="orange.100"
        bg="orange.50"
        shadow="sm"
        overflow="hidden"
      >
        {displaySubjectCreation ? (
          <Box px={[5, 6]} py={[4, 5]}>
            <SubjectCreationTimer
              title="Get more bookings faster"
              icon={<CIcon as={BiRocket} color="orange.400" fontSize="5xl" />}
            >
              <Box>
                <Box mb={6}>
                  Many times, clients want tutors who can take a number of
                  associated subjects. Adding related subjects can help you get
                  more bookings faster.
                </Box>
                <Button colorScheme={"blue"} variant="link" onClick={onOpen}>
                  Add related subjects
                </Button>
              </Box>
            </SubjectCreationTimer>
          </Box>
        ) : (
          <Box px={[5, 6]} py={[4, 5]}>
            <SubjectCreationTimer
              title="Add subjects to get more bookings"
              icon={<CIcon as={BiRocket} color="orange.400" fontSize="5xl" />}
            >
              <Box>
                <Box mb={6}>
                  Many times, clients want tutors who can take a number of
                  related subjects. Adding related subjects can help you get
                  more bookings faster.
                </Box>
              </Box>
            </SubjectCreationTimer>
          </Box>
        )}
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={"xl"}
        closeOnOverlayClick={false}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent mx={2}>
          <ModalHeader borderBottomWidth="2xs" borderColor="neutral.500">
            {isDetails ? "Add one or more subjects" : "Select your focus area"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            {isDetails ? (
              <SubjectGroupDetails
                store={store}
                selectSubject={(vals) => store.selectSubjectGroups(vals)}
                currentGroup={store.currentGroup}
              />
            ) : (
              <SubjectGroupList
                store={store}
                displayDetails={(groupItem) => {
                  setIsDetails(true);
                  store.setCurrentGroup(groupItem.title);
                }}
                groups={store.generatedGroups}
              />
            )}
          </ModalBody>

          {isDetails && (
            <ModalFooter
              display="block"
              borderTopWidth="2xs"
              borderColor="neutral.500"
            >
              <Flex justifyContent={"space-between"} alignItems="center">
                <Button
                  colorScheme="gray"
                  variant="link"
                  onClick={() => {
                    setIsDetails(false);
                  }}
                >
                  Back
                </Button>
                <Text>
                  {store.selectedSubjectGroups.length}{" "}
                  {store.selectedSubjectGroups.length <= 1
                    ? `subject`
                    : `subjects`}
                </Text>
                <Button
                  colorScheme={"blue"}
                  isDisabled={store.selectedSubjectGroups.length < 1}
                  onClick={async () => {
                    await store.saveSelectedSubjectGroups();
                    setIsDetails(false);
                    onClose();
                  }}
                  isLoading={store.loading}
                >
                  Add
                </Button>
              </Flex>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>
    </Stack>
  );
});

export const SubjectSearchDisplay = observer(
  ({
    subjects,
    onSelect,
    resetSearch,
    searchTerm,
    subjectIsSelected,
    cantAddSubject,
    selectedSubjects,
  }: {
    subjects: Array<{ category: string; subcategory: string; name: string }>;
    onSelect: (params?: any) => void;
    resetSearch: () => void;
    searchTerm: string;
    subjectIsSelected: (...params) => boolean;
    cantAddSubject: (...params) => boolean;
    selectedSubjects: Array<any>;
  }) => {
    const { toastData } = subjectData.subjectsPage;
    const successStatus = toastData.successStatus as "success";
    function onSubjectSelect(subject) {
      if (subject.status !== "denied") {
        let toastObject = {
          title: `${subject.name} ${toastData.selectedTitle}`,
        };

        if (subjectIsSelected(selectedSubjects, subject)) {
          toastObject = {
            ...toastObject,
            title: `${subject.name} ${toastData.deselectedTitle}`,
          };
        }
        onSelect(subject);
        displayToast({
          status: successStatus,
          title: toastObject.title,
          duration: 2000,
          position: "top",
        });
        resetSearch();
      }
    }
    return (
      <Box px={{ base: 5, md: 0 }}>
        {subjects.length > 0 ? (
          <Stack>
            <Text fontWeight="bold" fontSize="xl">
              Showing {Pluralize("result", subjects.length, true)}
            </Text>

            <Box
              as="ul"
              maxHeight="400px"
              borderWidth="2xs"
              borderColor="neutral.500"
              rounded="lg"
              boxShadow="md"
              overflow="auto"
            >
              {subjects.map(
                (
                  subject: {
                    name: string;
                    category: string;
                    subcategory: string;
                    status?: string;
                  },
                  index,
                ) => {
                  const isNotSelectable =
                    cantAddSubject(subject.status) ||
                    subject.status === "denied";
                  const isSelected =
                    subjectIsSelected(selectedSubjects, subject) ||
                    cantAddSubject(subject.status);
                  return (
                    <Flex
                      as="li"
                      borderBottomWidth={
                        index !== subjects.length ? "2xs" : "none"
                      }
                      borderColor="neutral.500"
                      cursor="pointer"
                      py={[3, 4]}
                      px={[4, 6]}
                      key={index}
                      _hover={{
                        cursor: isNotSelectable ? "not-allowed" : "pointer",
                        bg: isNotSelectable ? "neutral.200" : "blue.50",
                      }}
                      bg={isNotSelectable ? "neutral.200" : "white"}
                      justifyContent="space-between"
                      onClick={() => {
                        if (!isNotSelectable) {
                          onSubjectSelect(subject);
                        }
                      }}
                    >
                      <HStack align="flex-start">
                        <Box
                          data-checked={isSelected ? "" : undefined}
                          mt={1}
                          fontSize="2xl"
                          _checked={{
                            color: "blue.500",
                          }}
                          color="gray.300"
                        >
                          {isSelected || isNotSelectable ? (
                            <MdCheckBox
                              color={isNotSelectable ? "#BABEC2" : "#0054E5"}
                            />
                          ) : (
                            <MdCheckBoxOutlineBlank />
                          )}
                        </Box>

                        <Box>
                          <Text>{subject.name}</Text>
                          <Text
                            fontSize="sm"
                            color="gray.600"
                          >{`in ${subject.category}`}</Text>
                        </Box>
                      </HStack>

                      <Box mt={2}>
                        <Icon name="enter" />
                      </Box>
                    </Flex>
                  );
                },
              )}
            </Box>
          </Stack>
        ) : (
          <Stack mt={8} spacing={3}>
            <Text fontWeight="bold" fontSize="xl">
              No subjects found for <em>{searchTerm}</em>
            </Text>

            <Text>Select a subject from the category list</Text>
          </Stack>
        )}
      </Box>
    );
  },
);

const formatSubjectName = (subject) => {
  let subjectName = subject;
  if (subject === "Handwriting Improvement") {
    subjectName = "Handwriting";
  }
  if (subject === "Principles of Accounting") {
    subjectName = "Accounting";
  }
  return subjectName;
};

export const SubjectTabsDisplay = observer(
  ({ store }: { store: ISubjectStore }) => {
    const { getCheckboxProps } = useCheckboxGroup();
    const {
      allFilteredSubjects: subjects,
      subjectIsSelected,
      cantAddSubject,
      selectSubject,
      selectedSubjects,
      defaultTabIndex,
    } = store;
    const { toastData } = subjectData.subjectsPage;
    const successStatus = toastData.successStatus as "success";

    function onSubjectSelect(subjectObject) {
      let toastObject = {
        title: `${subjectObject.name} ${toastData.selectedTitle}`,
      };

      if (store.subjectIsSelected(store.selectedSubjects, subjectObject)) {
        toastObject = {
          ...toastObject,
          title: `${subjectObject.name} ${toastData.deselectedTitle}`,
        };
      }
      selectSubject(subjectObject);
      displayToast({
        status: successStatus,
        title: toastObject.title,
        duration: 3000,
        position: "top",
      });
    }
    return (
      <Stack spacing={10} p={{ base: 5, md: 8 }}>
        {subjects.map((subject, index) => {
          return (
            <Box key={index}>
              <Stack mb={2}>
                <Text fontSize="2xl" fontWeight="bold">
                  {subject.category}
                </Text>
                <Text color="gray.600" fontSize="sm">
                  Scroll right to click on more subcategories
                </Text>
              </Stack>

              <Tabs
                borderColor="neutral.200"
                key={defaultTabIndex}
                defaultIndex={defaultTabIndex}
              >
                <TabList
                  overflowY="hidden"
                  overflowX="auto"
                  _focus={{ borderColor: "green.500" }}
                  height={12}
                  sx={{
                    "-ms-overflow-style": "none",
                    "scrollbar-width": "none",
                  }}
                >
                  {subject.subcategories.map((subcategory, index) => {
                    return (
                      <Tab
                        px={1}
                        mr={[4, 5]}
                        whiteSpace="nowrap"
                        sx={{
                          "::-webkit-scrollbar": {
                            display: "none",
                          },
                        }}
                        _focus={{ outline: "none" }}
                        _selected={{
                          fontWeight: "medium",
                          color: "blue.500",
                          borderBottomWidth: "0.25rem",
                          borderColor: "blue.500",
                        }}
                        key={index}
                      >
                        {subcategory.title}
                      </Tab>
                    );
                  })}
                </TabList>
                <TabPanels>
                  {subject.subcategories.map((subcategory, index) => {
                    return (
                      <TabPanel px={0} key={index}>
                        <SimpleGrid columns={[1, 2]} spacing={[2, 4]}>
                          {subcategory.subjects.map((subj, index) => {
                            let subjectObject = {
                              name: subj.name,
                              category: subject.category,
                              subcategory: subcategory.title,
                            };
                            let isSelected = subjectIsSelected(
                              selectedSubjects,
                              subjectObject,
                            );
                            let isChecked =
                              isSelected ||
                              cantAddSubject(subj.status) ||
                              subj.status === "denied";

                            return (
                              <SubjectCheckbox
                                key={index}
                                {...getCheckboxProps({ value: subj.name })}
                                defaultChecked={isChecked}
                                isChecked={isChecked}
                                isDisabled={
                                  cantAddSubject(subj.status) ||
                                  subj.status === "denied"
                                }
                                onChange={() => onSubjectSelect(subjectObject)}
                              >
                                {formatSubjectName(subj.name)}
                              </SubjectCheckbox>
                            );
                          })}
                        </SimpleGrid>
                      </TabPanel>
                    );
                  })}
                </TabPanels>
              </Tabs>
            </Box>
          );
        })}
      </Stack>
    );
  },
);

export const SubjectActions: React.FC<{
  status: string;
  totalAttempts: number;
  testable: boolean;
  onTakeTest: () => void;
  onEdit: () => void;
}> = observer(({ status, totalAttempts, testable, onTakeTest, onEdit }) => {
  const { tableData } = subjectData.subjectsPage;
  if (status === "in-progress") {
    return (
      <Link cursor="pointer" color="blue.400" onClick={onEdit}>
        {tableData.finishButton}
      </Link>
    );
  }
  if (status === "not-started") {
    return (
      <Link cursor="pointer" color="blue.400" onClick={onTakeTest}>
        {tableData.takeTest}
      </Link>
    );
  }

  // if (status === "denied" && testable) {
  //   return (
  //     <Box cursor="pointer" color="blue.500">
  //       {totalAttempts > 2 ? (
  //         <Box>{tableData.naButton}</Box>
  //       ) : (
  //         <Link onClick={onTakeTest}>{tableData.retryTestButton}</Link>
  //       )}
  //     </Box>
  //   );
  // }
  if (status === "denied") {
    return null;
  }

  return (
    <Link cursor="pointer" color="blue.400" onClick={onEdit}>
      {tableData.editButton}
    </Link>
  );
});

const FilterDropdowns: React.FC<{
  categoryOptions: Array<string>;
  onCategoryChange: (category: string) => any;
  onSubcategoryChange: (subcategory: string) => any;
  subcategoryOptions: Array<{ label: string; value: string }>;
}> = observer(
  ({
    categoryOptions,
    onCategoryChange,
    subcategoryOptions,
    onSubcategoryChange,
  }) => {
    const [category, setCategory] = React.useState("");
    const [subcategory, setSubcategory] = React.useState("");
    const categoryRef = React.useRef();
    const subcategoryRef = React.useRef();
    return (
      <HStack spacing={3}>
        <Select
          id="categories"
          size="lg"
          ref={categoryRef}
          bg="neutral.100"
          placeholder="Category"
          borderColor="neutral.500"
          fontSize="md"
          defaultValue={category}
          onChange={(e) => {
            const value = e.target.value;
            setCategory(value);
            onCategoryChange(value);
          }}
        >
          {categoryOptions.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </Select>

        <Select
          id="subcategories"
          ref={subcategoryRef}
          size="lg"
          borderColor="neutral.500"
          fontSize="md"
          bg="neutral.100"
          placeholder={"Subcategory"}
          defaultValue={subcategory}
          onChange={(e) => {
            const value = e.target.value;
            setSubcategory(value);
            onSubcategoryChange(value);
          }}
        >
          {subcategoryOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </HStack>
    );
  },
);

const SubjectAdditionFilter: React.FC<{
  onInputSearchChange: (value: string) => any;
  categories: any[];
  subcategories: any[];
  searchTerm: string;
  onCategoryChange: (category: string) => any;
  onSubcategoryChange: (subcategory: string) => any;
}> = observer(
  ({
    onInputSearchChange,
    categories,
    subcategories,
    onCategoryChange,
    onSubcategoryChange,
    searchTerm,
  }) => {
    const { subjectAddition } = subjectData.subjectsPage;
    return (
      <Flex direction={["column", "row"]} justifyContent="space-between">
        <InputGroup flex={1} mr={[0, 3]}>
          <InputLeftElement
            pointerEvents="none"
            children={<Search2Icon mt={2} fontSize="lg" color="gray.300" />}
          />
          <Input
            onChange={({ target }: { target: any }) => {
              onInputSearchChange(target.value);
            }}
            size="lg"
            fontSize="md"
            value={searchTerm}
            bg="neutral.100"
            placeholder={subjectAddition.placeholder}
            borderColor="neutral.500"
          />
        </InputGroup>
        <Box mt={[4, 0]} flex={1} ml={[0, 3]}>
          <FilterDropdowns
            categoryOptions={categories}
            onCategoryChange={onCategoryChange}
            subcategoryOptions={subcategories}
            onSubcategoryChange={onSubcategoryChange}
          />
        </Box>
      </Flex>
    );
  },
);

const SubjectAdditionFooter: React.FC<{
  onClear: any;
  totalSubjectsSelected: number;
  tutorSubjects: number;
  isLoading: boolean;
  onSubmit: any;
  isDisabled: boolean;
  subjectsLeft?: number;
}> = observer(
  ({
    onClear,
    totalSubjectsSelected,
    isLoading,
    tutorSubjects,
    onSubmit,
    isDisabled,
    subjectsLeft,
  }) => {
    const { subjectAddition, toastData } = subjectData.subjectsPage;
    const successStatus = toastData.successStatus as "success";

    return (
      <Box>
        {isDisabled && subjectsLeft && (
          <Center width="full" py={2} bg="red.500" color="white">
            <HStack color="white">
              <Icon name="error" />
              <Text fontSize="sm">
                You can only add {subjectsLeft} more{" "}
                {Pluralize("subject", subjectsLeft)} for now
              </Text>
            </HStack>
          </Center>
        )}
        <Box
          bg="neutral.100"
          borderTopWidth="1px"
          borderBottomWidth={{ base: "1px", md: 0 }}
          px={{ base: 5, md: 8 }}
          py={{ base: 4, md: 5 }}
        >
          <Flex justifyContent="space-between" align="center">
            <Button
              variant="outline"
              colorScheme="red"
              onClick={() => {
                onClear();
                displayToast({
                  status: successStatus,
                  title: toastData.clearSubjectTitle,
                  duration: 3000,
                  position: "top",
                });
              }}
            >
              {subjectAddition.clearButton}
            </Button>
            {subjectsLeft ? (
              <Text
                fontWeight="medium"
                fontSize={["xs", "sm"]}
                color="gray.600"
              >
                {totalSubjectsSelected} /{" "}
                {Pluralize("subject", subjectsLeft, true)}
              </Text>
            ) : (
              <Text
                fontWeight="medium"
                fontSize={["xs", "sm"]}
                color="gray.600"
              >
                {Pluralize("subject", totalSubjectsSelected, true)}
              </Text>
            )}

            <Button
              colorScheme="blue"
              isDisabled={isDisabled}
              isLoading={isLoading}
              onClick={onSubmit}
            >
              {subjectAddition.doneButton}
            </Button>
          </Flex>
        </Box>
      </Box>
    );
  },
);

const SubjectAddition = observer(
  ({
    store,
    onSubmit,
    loading,
    onBackClick,
  }: {
    store: ISubjectStore;
    onSubmit: () => void;
    onBackClick: () => void;
    loading: boolean;
  }) => {
    const [searchTerm, setSearchTerm] = React.useState("");
    return (
      <Box as="section">
        <Box px={{ base: 5, md: 0 }}>
          <Button
            ml={-1}
            mb={{ base: 8, md: 10 }}
            size={useBreakpointValue({ base: "sm", md: "md" })}
            iconSpacing={1}
            colorScheme="blue"
            leftIcon={<ChevronLeftIcon fontSize={{ base: "xl", md: "2xl" }} />}
            variant="link"
            onClick={onBackClick}
          >
            Back
          </Button>
        </Box>
        <Stack spacing={6} position="relative">
          <Stack px={{ base: 5, md: 0 }}>
            <Text fontWeight="bold" fontSize="3xl">
              {subjectData.subjectsPage.subjectAddition.header}
            </Text>

            <Text color="gray.600">
              {subjectData.subjectsPage.subjectAddition.description}
            </Text>
          </Stack>

          <Stack px={{ base: 5, md: 0 }}>
            <SubjectAdditionFilter
              onInputSearchChange={setSearchTerm}
              searchTerm={searchTerm}
              categories={store.categories}
              onCategoryChange={(category) => {
                setSearchTerm("");
                store.onCategoryChange(category);
              }}
              onSubcategoryChange={(subcategory) => {
                setSearchTerm("");
                store.onSubcategoryChange(subcategory);
              }}
              subcategories={store.subcategoryOptions(
                store.filterTerms.category,
              )}
            />
          </Stack>

          {searchTerm.length > 1 ? (
            <SubjectSearchDisplay
              subjects={store.searchedSubjects(searchTerm)}
              cantAddSubject={store.cantAddSubject}
              searchTerm={searchTerm}
              onSelect={(subject) => store.selectSubject(subject)}
              resetSearch={() => setSearchTerm("")}
              selectedSubjects={store.selectedSubjects}
              subjectIsSelected={store.subjectIsSelected}
            />
          ) : (
            <Box>
              <Stack
                spacing={6}
                borderWidth={{ md: "1px" }}
                overflow="hidden"
                rounded={{ md: "lg" }}
              >
                <SubjectTabsDisplay store={store} />
                <SubjectAdditionFooter
                  isLoading={loading}
                  onSubmit={onSubmit}
                  totalSubjectsSelected={store.selectedSubjects.length}
                  tutorSubjects={store.tutorSubjects.length}
                  onClear={store.clearSelectedSubjects}
                  isDisabled={!store.canAddMoreSubjects}
                  subjectsLeft={store.subjectsLeft}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    );
  },
);

export default SubjectAddition;
